import { useMemo } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { usePatchLoanEmploymentMutation } from '@/apis/extended/loanApplicationApi';
import {
  EMPLOYMENT_PAY_SCHEDULE_LABELS,
  grossMonthlyFromPaySchedule,
} from '@/utils/incomeFromPaySchedule';
import type {
  LoanApplication,
  LoanEmploymentPaymentSchedule,
  LoanEmploymentRow,
} from '@/types/LoanApplicationType';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { useLoanSectionForm } from './useLoanSectionForm';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
};

const EMPLOYMENT_STATUS = [
  'Employed',
  'Self-employed',
  'Retired',
  'Military',
  'Other',
] as const;

const PAY_SCHEDULE_NON_EMPTY = [
  'hourly',
  'daily',
  'weekly',
  'bi_weekly',
  'semi_monthly',
  'monthly',
] as const satisfies readonly Exclude<LoanEmploymentPaymentSchedule, ''>[];

const emptyRow = (): LoanEmploymentRow => ({
  employerName: '',
  jobTitle: '',
  startDate: '',
  monthlyIncome: 0,
  monthlyBaseIncome: null,
  monthlyOvertime: null,
  monthlyBonus: null,
  employmentStatus: '',
  isCurrent: true,
  paymentSchedule: '',
  periodGrossPay: null,
  hoursPerWeek: null,
  workDaysPerWeek: null,
});

export function EmploymentSection({ application, readOnly }: Props) {
  const [patch, { isLoading }] = usePatchLoanEmploymentMutation();

  const initialRows = useMemo(
    () =>
      application.employment?.length
        ? application.employment.map((r) => ({ ...r }))
        : [],
    [application._id, application.employment, application.updatedAt]
  );

  const {
    draft: rows,
    setDraft: setRows,
    isDirty,
    markSaved,
    resetDraft,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: 'employment',
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: initialRows,
    readOnly,
  });

  const saveSection = async () => {
    if (formReadOnly) return;
    try {
      await patch({
        id: application._id,
        employment: rows,
      }).unwrap();
      markSaved(rows);
      toast.success('Saved');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const disabled = formReadOnly || isLoading;

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Employment history (optional for DSCR-heavy files; useful for other
        products).
      </Typography>
      {rows.length === 0 && !formReadOnly && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          No employers listed — add one if applicable.
        </Typography>
      )}
      {rows.map((row, idx) => (
        <Grid container spacing={2} key={row._id ?? idx} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Employer"
              value={row.employerName}
              disabled={disabled}
              onChange={(e) => {
                const n = [...rows];
                n[idx] = { ...row, employerName: e.target.value };
                setRows(n);
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Title"
              value={row.jobTitle}
              disabled={disabled}
              onChange={(e) => {
                const n = [...rows];
                n[idx] = { ...row, jobTitle: e.target.value };
                setRows(n);
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Start"
              type="date"
              value={row.startDate}
              disabled={disabled}
              onChange={(e) => {
                const n = [...rows];
                n[idx] = { ...row, startDate: e.target.value };
                setRows(n);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Monthly income"
              type="number"
              value={row.monthlyIncome}
              disabled={disabled}
              onChange={(e) => {
                const n = [...rows];
                n[idx] = {
                  ...row,
                  monthlyIncome: Number(e.target.value) || 0,
                };
                setRows(n);
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              label="Pay schedule"
              value={row.paymentSchedule ?? ''}
              disabled={disabled}
              onChange={(e) => {
                const n = [...rows];
                n[idx] = {
                  ...row,
                  paymentSchedule: e.target
                    .value as LoanEmploymentPaymentSchedule,
                };
                setRows(n);
              }}
            >
              <MenuItem value="">—</MenuItem>
              {PAY_SCHEDULE_NON_EMPTY.map((k) => (
                <MenuItem key={k} value={k}>
                  {EMPLOYMENT_PAY_SCHEDULE_LABELS[k]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {(row.paymentSchedule === 'hourly' ||
            row.paymentSchedule === 'daily' ||
            row.paymentSchedule === 'weekly' ||
            row.paymentSchedule === 'bi_weekly' ||
            row.paymentSchedule === 'semi_monthly' ||
            row.paymentSchedule === 'monthly') && (
            <>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label={
                    row.paymentSchedule === 'hourly'
                      ? 'Hourly rate'
                      : row.paymentSchedule === 'daily'
                      ? 'Daily gross'
                      : row.paymentSchedule === 'weekly'
                      ? 'Weekly gross'
                      : row.paymentSchedule === 'bi_weekly'
                      ? 'Bi-weekly gross'
                      : row.paymentSchedule === 'semi_monthly'
                      ? 'Semi-monthly gross'
                      : 'Monthly gross'
                  }
                  type="number"
                  value={row.periodGrossPay ?? ''}
                  disabled={disabled}
                  onChange={(e) => {
                    const n = [...rows];
                    n[idx] = {
                      ...row,
                      periodGrossPay:
                        e.target.value === '' ? null : Number(e.target.value),
                    };
                    setRows(n);
                  }}
                />
              </Grid>
              {row.paymentSchedule === 'hourly' && (
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Hours / week"
                    type="number"
                    value={row.hoursPerWeek ?? ''}
                    placeholder="40"
                    disabled={disabled}
                    onChange={(e) => {
                      const n = [...rows];
                      n[idx] = {
                        ...row,
                        hoursPerWeek:
                          e.target.value === '' ? null : Number(e.target.value),
                      };
                      setRows(n);
                    }}
                  />
                </Grid>
              )}
              {row.paymentSchedule === 'daily' && (
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Work days / week"
                    type="number"
                    placeholder="5"
                    value={row.workDaysPerWeek ?? ''}
                    disabled={disabled}
                    onChange={(e) => {
                      const n = [...rows];
                      n[idx] = {
                        ...row,
                        workDaysPerWeek:
                          e.target.value === '' ? null : Number(e.target.value),
                      };
                      setRows(n);
                    }}
                  />
                </Grid>
              )}
              <Grid
                item
                xs={12}
                md={
                  row.paymentSchedule === 'hourly' ||
                  row.paymentSchedule === 'daily'
                    ? 12
                    : 6
                }
              >
                <Typography variant="body2" color="text.secondary">
                  {(() => {
                    const amt = row.periodGrossPay;
                    if (amt == null || !Number.isFinite(amt)) {
                      return 'Enter pay details to estimate monthly income.';
                    }
                    const est = grossMonthlyFromPaySchedule(
                      row.paymentSchedule ?? '',
                      {
                        amount: amt,
                        hoursPerWeek: row.hoursPerWeek,
                        workDaysPerWeek: row.workDaysPerWeek,
                      }
                    );
                    if (est == null) return null;
                    return (
                      <>
                        Estimated monthly (gross):{' '}
                        <strong>${est.toLocaleString()}</strong>
                        {!formReadOnly && (
                          <>
                            {' '}
                            <Button
                              size="small"
                              variant="text"
                              disabled={isLoading}
                              onClick={() => {
                                const n = [...rows];
                                n[idx] = { ...row, monthlyIncome: est };
                                setRows(n);
                              }}
                            >
                              Use for monthly income
                            </Button>
                          </>
                        )}
                      </>
                    );
                  })()}
                </Typography>
              </Grid>
            </>
          )}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              label="Employment status"
              value={row.employmentStatus ?? ''}
              disabled={disabled}
              onChange={(e) => {
                const n = [...rows];
                n[idx] = { ...row, employmentStatus: e.target.value };
                setRows(n);
              }}
            >
              <MenuItem value="">—</MenuItem>
              {EMPLOYMENT_STATUS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Base / mo"
              type="number"
              value={row.monthlyBaseIncome ?? ''}
              disabled={disabled}
              onChange={(e) => {
                const n = [...rows];
                n[idx] = {
                  ...row,
                  monthlyBaseIncome:
                    e.target.value === '' ? null : Number(e.target.value),
                };
                setRows(n);
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Overtime / mo"
              type="number"
              value={row.monthlyOvertime ?? ''}
              disabled={disabled}
              onChange={(e) => {
                const n = [...rows];
                n[idx] = {
                  ...row,
                  monthlyOvertime:
                    e.target.value === '' ? null : Number(e.target.value),
                };
                setRows(n);
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Bonus / mo"
              type="number"
              value={row.monthlyBonus ?? ''}
              disabled={disabled}
              onChange={(e) => {
                const n = [...rows];
                n[idx] = {
                  ...row,
                  monthlyBonus:
                    e.target.value === '' ? null : Number(e.target.value),
                };
                setRows(n);
              }}
            />
          </Grid>
          {!formReadOnly && (
            <Grid item xs={12}>
              <IconButton
                onClick={() => setRows((R) => R.filter((_, i) => i !== idx))}
              >
                <i className="fa-light fa-trash" />
              </IconButton>
            </Grid>
          )}
        </Grid>
      ))}
      {!formReadOnly && (
        <Box sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setRows((R) => [...R, emptyRow()])}
          >
            Add employer
          </Button>
        </Box>
      )}
      <LoanSectionFormFooter
        readOnly={formReadOnly}
        isDirty={isDirty}
        isSaving={isLoading}
        saveLabel="Save employment"
        onSave={() => void saveSection()}
        onCancel={resetDraft}
      />
    </div>
  );
}
